import { useSearchParams } from 'react-router-dom';

const errorDescriptions: Record<string, string> = {
  invalid_request: 'The request is missing a required parameter or is otherwise malformed.',
  unauthorized_client: 'The client is not authorized to request an authorization code.',
  access_denied: 'The resource owner or authorization server denied the request.',
  unsupported_response_type: 'The authorization server does not support this response type.',
  invalid_scope: 'The requested scope is invalid, unknown, or malformed.',
  server_error: 'The authorization server encountered an unexpected condition.',
  temporarily_unavailable: 'The authorization server is currently unable to handle the request.',
  invalid_client: 'Client authentication failed.',
  invalid_grant: 'The provided authorization grant is invalid, expired, or revoked.',
  unsupported_grant_type: 'The authorization grant type is not supported.',
};

export default function ErrorPage() {
  const [searchParams] = useSearchParams();

  const error = searchParams.get('error') || 'unknown_error';
  const errorDescription =
    searchParams.get('error_description') || errorDescriptions[error] || 'An unknown error occurred.';
  const errorUri = searchParams.get('error_uri');
  const state = searchParams.get('state');

  const getErrorIcon = () => {
    switch (error) {
      case 'access_denied':
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
            />
          </svg>
        );
      case 'invalid_client':
      case 'unauthorized_client':
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        );
      case 'server_error':
      case 'temporarily_unavailable':
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      default:
        return (
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-header">
        <div className="auth-logo">ZoAuth</div>
        <div className="auth-subtitle">Authorization Error</div>
      </div>

      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div
          style={{
            width: '64px',
            height: '64px',
            color: 'var(--error-color)',
            margin: '0 auto',
          }}
        >
          {getErrorIcon()}
        </div>

        <h2
          style={{
            marginTop: '16px',
            marginBottom: '8px',
            color: 'var(--error-color)',
            fontSize: '20px',
          }}
        >
          {error.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
        </h2>

        <p
          style={{
            color: 'var(--text-secondary)',
            fontSize: '14px',
            lineHeight: '1.6',
          }}
        >
          {errorDescription}
        </p>

        {state && (
          <p
            style={{
              marginTop: '12px',
              fontSize: '12px',
              color: 'var(--text-secondary)',
              fontFamily: 'monospace',
            }}
          >
            State: {state}
          </p>
        )}
      </div>

      <div style={{ marginTop: '32px' }}>
        {errorUri ? (
          <a
            href={errorUri}
            className="btn btn-primary"
            style={{ textDecoration: 'none', display: 'block' }}
          >
            Learn More
          </a>
        ) : (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => (window.location.href = '/login')}
          >
            Return to Sign In
          </button>
        )}
      </div>

      <div className="footer-links">
        <a href="https://support.zoauth.com" className="footer-link">
          Contact Support
        </a>
        <span style={{ color: 'var(--text-secondary)' }}>·</span>
        <a href="https://docs.zoauth.com/errors" className="footer-link">
          Error Reference
        </a>
      </div>
    </div>
  );
}
